// Cartesian tile. V-shape-native: resolves every series'
// `:x`/`:y` `ColumnRef` to columns, merges them into a recharts data
// array keyed by x, and renders with recharts primitives
// (Line/Bar/Area + reference markers). Colours map
// through the existing `mantineColorVar` helper.

import type { CSSProperties } from 'react';
import { useId } from 'react';
import { ResponsiveContainer } from 'recharts';

import { formatNumber, formatTick, numberHints } from '#core/components/dashboard/number-format.ts';
import type { CartesianSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import { formatDateValue } from '#core/lib/expr/ops/transform/date-utils.ts';
import type { ColumnRef } from '#core/lib/expr/value.ts';
import { csvFromRows } from '#core/lib/transforms/csv-from-rows.ts';
import { slugify } from '#core/lib/utils/slugify.ts';

import { ladderedUnitDisplay, unitLabel } from '../unit-label.ts';
import classes from './CartesianPanel.module.css';
import { type LabelFormatter, pickDateFormat, pickTooltipDateFormat } from './cartesian/axisLabels.tsx';
import { CartesianChart } from './cartesian/CartesianChart.tsx';
import { cartesianRows } from './cartesian/cartesianRows.ts';
import { chartOrientationModel } from './cartesian/chartOrientation.ts';
import { TileFrame } from './TileFrame.tsx';
import { useResolvedColumns } from './useResolvedColumns.ts';

/** Display unit for a series' value column, or '' when the column is unit-less. */
const refUnit = (ref: ColumnRef | undefined): string => unitLabel(ref?.ds.schema.get(ref.column));
/**
 * Outer/inner wrapper attributes for a cartesian tile's two-layer sizing
 * policy. Returned as spreadable prop bundles so the caller doesn't need
 * to know whether a mode is encoded as a data attribute, a CSS custom
 * property, or both.
 */
interface AspectAttrs {
  readonly style?: CSSProperties;
  readonly 'data-orientation'?: 'horizontal';
}

/**
 * Two-layer sizing policy for a cartesian tile. The constants
 * (aspect ratio, min/max heights, bar slot, axis chrome) live in
 * `CartesianPanel.module.css` — this function only emits the
 * mode discriminator and the per-row data length used by the
 * CSS-side `max(.., calc(...))` height formula.
 *
 * - Vertical orientation (default): aspect-locked. Emits nothing —
 *   the CSS class's base rules carry the aspect-ratio / clamps.
 * - Horizontal orientation: emits `data-orientation='horizontal'`
 *   on the outer wrapper (selects the height-driven rule set) and
 *   `--cartesian-data-len: <dataLen>` as a CSS custom property on the
 *   inner wrapper (consumed by the CSS `max(MIN, calc(N * SLOT + CHROME))`
 *   height computation).
 *
 * Returning `outer`/`inner` separately keeps the scroll viewport
 * decoupled from the chart's content height — the chart never
 * scrolls inside its own painted area, only the outer wrapper does.
 */
function aspectStyleFor(
  spec: CartesianSpec,
  dataLen: number,
): { readonly outer: AspectAttrs; readonly inner: AspectAttrs } {
  if (spec.orientation === 'horizontal') {
    return {
      outer: { 'data-orientation': 'horizontal' },
      inner: { style: { '--cartesian-data-len': String(dataLen) } as CSSProperties },
    };
  }
  return { outer: {}, inner: {} };
}

export function CartesianPanel({
  spec,
  citedMarkers,
}: {
  spec: CartesianSpec;
  /** Labels of reference markers cited by insights — rendered emphasized. */
  citedMarkers?: ReadonlySet<string>;
}) {
  const tileId = useId();
  // Resolve every series x/y ref AND every band x/lower/upper ref in one
  // stable-length call — the hook count must not depend on how many
  // series or bands a tile has (React rules-of-hooks).
  const cols = useResolvedColumns([
    ...spec.series.flatMap((s) => [s.x, s.y]),
    ...spec.bands.flatMap((b) => [b.x, b.lower, b.upper]),
  ]);
  // Resolve the orientation model once at the panel layer — it drives
  // axis rendering (band vs numeric scale) and reference-marker
  // coordinates. Threaded down to CartesianChart as a prop.
  const o = chartOrientationModel(spec);
  const isTime = spec.xKind === 'time';
  // Merge series + bands into recharts rows keyed by the shared x value,
  // ordered by the x domain (time/numeric sort; category keeps row order).
  const data = cartesianRows(spec, cols);
  const { outer, inner } = aspectStyleFor(spec, data.length);
  const valuesForSeries = (ids: readonly string[]): number[] =>
    data.flatMap((row) => ids.map((id) => row[id]).filter((v): v is number => typeof v === 'number'));
  const combinedValues = valuesForSeries(spec.series.map((s) => s.id));
  const makeValueFormatter = (fmtNumber: (n: number) => string) => {
    const f = (value: number | string | readonly (string | number)[] | undefined): string => {
      if (value === undefined) {
        return 'n/a';
      }
      if (typeof value === 'string') {
        return value;
      }
      if (Array.isArray(value)) {
        return value.map(f).join(', ');
      }
      return fmtNumber(value as number);
    };
    return f;
  };
  // Per-axis laddered display. Every value axis carries a single unit (the
  // contract gives each unit its own axis), so the axis fixes ONE ladder rung
  // from the combined values of all its series — folding magnitude into the
  // petroleum unit prefix so ticks and tooltips on that axis stay comparable.
  // Undefined for unit-less / fixed-symbol dimensions — those keep `formatTick`.
  const axisLaddered = (side: 'left' | 'right') => {
    const onSide = spec.series.filter((s) => (s.axis ?? 'left') === side);
    const first = onSide.at(0)?.y;
    const colType = first === undefined ? undefined : first.ds.schema.get(first.column);
    return ladderedUnitDisplay(colType, valuesForSeries(onSide.map((s) => s.id)));
  };
  const leftLaddered = axisLaddered('left');
  const rightLaddered = axisLaddered('right');
  const ladderedBySide = { left: leftLaddered, right: rightLaddered } as const;
  const valueFormatter = makeValueFormatter((v) => formatNumber(v, numberHints(combinedValues)));
  // Per-tick value-axis formatter: a laddered axis scales each tick onto its
  // fixed rung; otherwise each tick is formatted by its own magnitude so an
  // axis spanning large and small series keeps small-tick decimals while large
  // ticks keep their thousand separator.
  const valueTickFormatterFor = (side: 'left' | 'right') => {
    const ladder = ladderedBySide[side];
    return makeValueFormatter(ladder ? ladder.format : formatTick);
  };
  // Two formatters for the logical category dimension (`x`), both
  // orientation-independent and x-kind aware (time → date,
  // numeric/category → number/string):
  //   axisTickFormatter — sparse and adaptive, tuned for axis labels.
  //     Picked from the *visible range* so a multi-year chart shows
  //     `'YY-MM'` while a sub-day chart shows `'HH:mm'`.
  //   tooltipLabelFormatter — maximally precise, must always identify
  //     a single datapoint regardless of chart range. Picked from the
  //     *finest delta* in the data so daily series get `'YYYY-MM-DD'`,
  //     hourly get `'YYYY-MM-DD HH:mm'`, sub-minute get seconds.
  // The two formatters MUST NOT share a pattern — if the tooltip on a
  // multi-month chart inherits the sparse axis pattern (`'YY-MM'`) it
  // hides individual datapoint dates.
  const xNumbers = isTime ? data.map((r) => r.x).filter((v): v is number => typeof v === 'number') : [];
  const dateRangeMs = xNumbers.length > 1 ? Math.max(...xNumbers) - Math.min(...xNumbers) : 0;
  const tickDatePattern = pickDateFormat(dateRangeMs);
  const tooltipDatePattern = pickTooltipDateFormat(xNumbers);
  const makeLabelFormatter =
    (pattern: string): LabelFormatter =>
    (value) => {
      if (value === undefined) {
        return 'n/a';
      }
      if (typeof value === 'number') {
        return isTime ? (formatDateValue(value, pattern) ?? String(value)) : formatTick(value);
      }
      return value?.toString() ?? 'n/a';
    };
  const axisTickFormatter = makeLabelFormatter(tickDatePattern);
  const tooltipLabelFormatter = makeLabelFormatter(tooltipDatePattern);
  // Snap ticks to the actual data points on sparse continuous axes —
  // recharts otherwise picks "nice" round numbers that miss the data.
  // Only meaningful when the category axis is continuous (number)
  // *and* the dataset is small enough that picking nice round numbers
  // visibly misses the data (a dense series picks ticks correctly).
  const snappedTicks =
    o.categoryNumeric && data.length > 0 && data.length <= 12
      ? data.map((r) => r.x).filter((v): v is number => typeof v === 'number')
      : undefined;

  const hasRightAxis = spec.series.some((s) => s.axis === 'right');
  const stacked = spec.stack !== 'none';
  // A stroke-only cursor is wrong on a bar chart — `OrientedTooltip`
  // needs to know whether any bar series is present.
  const hasBar = spec.series.some((s) => s.type === 'bar');

  // Declared units. Each value axis carries one unit; a laddered axis surfaces
  // its fixed-rung label (e.g. MMSCF/d), else the magnitude-independent symbol.
  // The tooltip appends the per-series unit and scales the value onto that
  // series' axis rung, keyed by the recharts dataKey (= series id) so multi-unit
  // charts read right.
  const leftUnit =
    (leftLaddered?.unit ?? refUnit(spec.series.find((s) => (s.axis ?? 'left') === 'left')?.y)) || undefined;
  const rightUnit = (rightLaddered?.unit ?? refUnit(spec.series.find((s) => s.axis === 'right')?.y)) || undefined;
  const sideOf = (id: string): 'left' | 'right' =>
    spec.series.find((s) => s.id === id)?.axis === 'right' ? 'right' : 'left';
  const unitByKey = new Map(
    spec.series.map((s) => [s.id, ladderedBySide[sideOf(s.id)]?.unit ?? refUnit(s.y)] as const),
  );
  const tooltipValueFormatter = (
    value: number | string | readonly (string | number)[] | undefined,
    _name?: unknown,
    item?: { readonly dataKey?: string | number },
  ): string => {
    const key = item?.dataKey === undefined ? undefined : String(item.dataKey);
    const ladder = key === undefined ? undefined : ladderedBySide[sideOf(key)];
    const base = ladder && typeof value === 'number' ? ladder.format(value) : valueFormatter(value);
    const unit = key === undefined ? '' : (unitByKey.get(key) ?? '');
    return unit ? `${base} ${unit}` : base;
  };

  // CSV view of the resolved data: project the panel's recharts-shaped
  // rows onto user-readable headers — the category column adopts the
  // tile's `xLabel` (formatted as ISO date on a time axis), the value
  // columns adopt each series' `label`. Band tuples are skipped — they
  // aren't a flat CSV concept and the underlying min/max columns already
  // export through their own tiles when configured as series.
  const getCsv = () =>
    csvFromRows(data, [
      {
        source: 'x',
        label: spec.xLabel ?? 'x',
        transform: isTime ? (v) => formatDateValue(v as number, 'YYYY-MM-DD HH:mm:ss') ?? v : undefined,
      },
      ...spec.series.map((s) => ({ source: s.id, label: s.label })),
    ]);
  const tileName = `cartesian-${slugify(spec.series.at(0)?.label ?? 'series')}`;

  return (
    <TileFrame getCsv={getCsv} id={tileId} inspect={() => ({ spec, data })} name={tileName}>
      <div className={classes.cartesianChart} {...outer}>
        <div {...inner}>
          <ResponsiveContainer>
            <CartesianChart
              axisTickFormatter={axisTickFormatter}
              citedMarkers={citedMarkers}
              data={data}
              hasBar={hasBar}
              hasRightAxis={hasRightAxis}
              leftUnit={leftUnit}
              o={o}
              rightUnit={rightUnit}
              spec={spec}
              stacked={stacked}
              ticks={snappedTicks}
              tooltipLabelFormatter={tooltipLabelFormatter}
              tooltipValueFormatter={tooltipValueFormatter}
              valueTickFormatterLeft={valueTickFormatterFor('left')}
              valueTickFormatterRight={valueTickFormatterFor('right')}
            />
          </ResponsiveContainer>
        </div>
      </div>
    </TileFrame>
  );
}
